'use client';
import { Modal, Button } from 'antd';
import React, { ChangeEvent, FormEvent } from 'react';
import styled from '@benzinga/themetron';
import fastStringify from 'fast-json-stable-stringify';
import { openWindow } from '@benzinga/frontend-utils';
import { Spinner } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { DesktopNotificationManager } from '@benzinga/desktop-notification-manager';

const PRODPAD_API_URL = 'https://api.prodpad.com/v1/feedbacks';

const PRODPAD_API_KEY = '4f53d732c286473e240b06c0fc0fcab3951718ef4cd6983d67e03dbc5dd26b7b';

const defaultLabelColor = '#9c9c9c';
const focusLabelColor = '#1693E6';

const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

interface OpenWindowOptions {
  errorMessage?: string;
  free?: boolean;
  onError?(): void;
}

const FirstLine = styled.h1`
  color: black;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: center;
`;

const SecondLine = styled.h2`
  color: black;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
`;

interface InputProps {
  $isError?: boolean;
}

interface FormFieldBlockProps {
  height: string;
  margin: string;
}

const FormFieldBlock = styled.div<FormFieldBlockProps>`
  height: ${props => props.height};
  position: relative;
  margin: 0 auto ${props => props.margin} auto;
  max-width: 100%;
  width: 570px;
`;

const ReportButton = styled(Button)`
  padding: 0 25px !important;
`;

const BlockInput = styled.input<InputProps>`
  border: 1px solid ${props => (props.$isError ? 'red' : '#dcdcdc')};
  border-radius: 4px;
  color: black;
  display: inline-block;
  font-size: 16px;
  height: 36px;
  line-height: 34px;
  margin-top: 10px;
  max-width: 100%;
  min-height: 38px;
  padding: 0px 14px;
  transition: border 0.15s ease-in-out;
  width: 570px;
  :focus {
    border-color: ${props => (props.$isError ? 'red' : '#1693E6')};
  }
`;

interface LabelProps {
  color: string;
  opacity: string;
}

const FormLabel = styled.label<LabelProps>`
  background: white;
  color: ${props => props.color};
  display: block;
  left: 10px;
  line-height: 20px;
  opacity: ${props => props.opacity};
  padding: 0 4px;
  position: absolute;
  top: 0;
  transition: opacity 0.15s ease-in-out;
  width: auto;
  z-index: 2;
`;

const InvalidFormField = styled.p`
  color: red;
  display: block;
  padding-left: 14px;
  padding-top: 2px;
`;

const TextArea = styled.textarea<InputProps>`
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  color: black;
  display: inline-block;
  font-size: 16px;
  height: 140px;
  line-height: 160%;
  margin-top: 10px;
  max-width: 100%;
  padding: 7px 14px;
  resize: none;
  transition: border 0.15s ease-in-out;
  width: 570px;
  :focus {
    border-color: #1693e6;
  }
`;

const SubmitButton = styled.button`
  color: white;
  background-color: #1693e6;
  border: 0;
  border-radius: 4px;
  display: block;
  font-family:
    Open Sans,
    Helvetica,
    Arial,
    sans-serif;
  font-size: 14px;
  height: 40px;
  line-height: 40px;
  margin: auto;
  min-width: 175px;
  padding: 0 16px;
  transition:
    color 0.15s linear,
    box-shadow 0.15s ease-in-out,
    background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out;

  :disabled {
    opacity: 0.3;
    cursor: default;
    box-shadow: none;
  }
`;

const ReportButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1em;
`;

const ReportButtonText = styled.span`
  font-size: 18px;
  font-weight: 600;
`;

const StyledSpinner = styled(Spinner)`
  color: yellow;
`;

interface Props {
  handleClose: () => void;
  isVisible: boolean;
  onSubmit: () => void;
}

export const FeedbackForm: React.FC<Props> = props => {
  const [description, setDescription] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [labelColors, setLabelColors] = React.useState<{ description: string; email: string; name: string }>({
    description: defaultLabelColor,
    email: defaultLabelColor,
    name: defaultLabelColor,
  });
  const [userEmail, setUserEmail] = React.useState<string>('');
  const [username, setUsername] = React.useState<string>('');
  const session = React.useContext(SessionContext);

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    setUserEmail(event.target.value);
  };

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const onChangeDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };

  const setDefaultState = () => {
    setUserEmail('');
    setUsername('');
    setDescription('');
    setLoading(false);
  };

  const submitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const reqBody = {
      email: userEmail,
      feedback: `${description}

Submitted from: ${window.location.origin}
Commit hash: ${process.env.GIT_COMMITHASH}
Version: ${process.env.GIT_VERSION}`,
      name: username,
    };

    const headers = {
      Authorization: PRODPAD_API_KEY,
      'Content-Type': 'application/json',
    };

    setLoading(true);

    const feedbackPromise = fetch(PRODPAD_API_URL, {
      body: fastStringify(reqBody),
      headers,
      method: 'POST',
      mode: 'cors',
    }).then(() => {
      setLoading(false);
      props.onSubmit();

      session.getManager(DesktopNotificationManager).showInternalNotification('Thank you!', {
        body: 'The Benzinga team appreciates your feedback and will review this shortly.',
      });
    });

    feedbackPromise.catch(() => {
      setLoading(false);
    });

    return false;
  };

  const isFilledName = () => {
    return username && username !== '';
  };
  const isFilledDescription = () => {
    return description && description !== '';
  };
  const isFilledEmail = () => {
    return userEmail && userEmail !== '';
  };

  const isValidEmail = () => !isFilledEmail() || validateEmail(userEmail);

  const isDisabledSubmit = () => !(isFilledDescription() && isFilledName() && isValidEmail());

  const getLabelColor = (field: string) => labelColors[field];
  const makeLabelColorSetter = (field: string, color: string) => () => {
    if (labelColors[field] !== color) {
      setLabelColors({ ...labelColors, [field]: color });
    }
  };

  const handleReportClick = () => {
    newIntercomWindow({
      onError: () => {
        window.location.href = 'mailto:support@benzinga.com';
      },
    });
    props.handleClose();
  };

  const newIntercomWindow = (config?: OpenWindowOptions) => {
    const url = '/helpchat/';
    const name = 'Benzinga Pro Help Chat';
    const options = 'width=425,height=550,left=0,resizable';
    openWindow(url, name, options, undefined, { free: true, ...(config ?? {}) });
  };

  const { handleClose, isVisible } = props;
  const emailLabelOpacity = isFilledEmail() ? '1' : '0';
  const descriptionLabelOpacity = isFilledDescription() ? '1' : '0';
  const nameLabelOpacity = isFilledName() ? '1' : '0';

  if (!isVisible) return null;

  return (
    <Modal
      afterClose={setDefaultState}
      bodyStyle={{ background: 'white' }}
      closable={true}
      confirmLoading={loading}
      footer={null}
      maskClosable={!loading}
      onCancel={handleClose}
      open={isVisible}
      width={1024}
      zIndex={1000}
    >
      <FirstLine>We love to hear your ideas!</FirstLine>
      <SecondLine>What would make Benzinga Pro better for you?</SecondLine>
      <ReportButtonWrapper>
        <ReportButton className="ant-btn-danger" onClick={handleReportClick} size="large">
          <ReportButtonText>Looking for Support or have a BUG to report? CLICK HERE</ReportButtonText>
        </ReportButton>
      </ReportButtonWrapper>
      <form action="#" onSubmit={submitForm}>
        <FormFieldBlock height="74px" margin="0">
          <BlockInput
            onBlur={makeLabelColorSetter('name', defaultLabelColor)}
            onChange={onChangeName}
            onFocus={makeLabelColorSetter('name', focusLabelColor)}
            placeholder="Name"
            value={username}
          />
          <FormLabel color={getLabelColor('name')} opacity={nameLabelOpacity}>
            Name
          </FormLabel>
          {isFilledName() ? null : <InvalidFormField>Uh-oh! You forgot to add your name.</InvalidFormField>}
        </FormFieldBlock>
        <FormFieldBlock height="48px" margin="24px">
          <BlockInput
            $isError={!isValidEmail()}
            onBlur={makeLabelColorSetter('email', defaultLabelColor)}
            onChange={onChangeEmail}
            onFocus={makeLabelColorSetter('email', focusLabelColor)}
            placeholder="Email"
            type="email"
            value={userEmail}
          />
          <FormLabel color={getLabelColor('email')} opacity={emailLabelOpacity}>
            Email
          </FormLabel>
        </FormFieldBlock>
        <FormFieldBlock height="184px" margin="0">
          <TextArea
            onBlur={makeLabelColorSetter('description', defaultLabelColor)}
            onChange={onChangeDescription}
            onFocus={makeLabelColorSetter('description', focusLabelColor)}
            placeholder="It would be awesome if..."
            value={description}
          />
          <FormLabel color={getLabelColor('description')} opacity={descriptionLabelOpacity}>
            It would be awesome if...
          </FormLabel>
          {isFilledDescription() ? null : <InvalidFormField>Uh-oh! You forgot to add your feedback.</InvalidFormField>}
        </FormFieldBlock>
        <SubmitButton disabled={isDisabledSubmit()}>{loading ? <StyledSpinner /> : 'Submit Feedback'}</SubmitButton>
      </form>
    </Modal>
  );
};
